'use client';

import { useEffect } from 'react';

declare global {
    interface Window {
        Goftino?: {
            open: () => void;
            close: () => void;
            toggle: () => void;
            destroy: () => void;
            reload: (opts?: unknown) => void;
            [key: string]: unknown;
        };
    }
}

const WIDGET_ID = 'E4GiF7';

// Module-level state: the widget script must load once, ever — a locale
// segment change remounts this component (Next.js remounts a layout's
// subtree when the layout's own dynamic param changes), but re-appending
// the bootstrap script would open a second connection. `reload()` itself
// re-fires 'goftino_ready', so desired/applied are tracked separately —
// otherwise reacting to that event by calling reload() again loops forever.
let scriptLoadStarted = false;
let desiredVisible = false;
let appliedVisible: boolean | null = null; // null = not applied yet

function ensureScriptLoaded() {
    if (scriptLoadStarted) return;
    scriptLoadStarted = true;

    const d = document;
    function g() {
        const s = d.createElement('script');
        const src = 'https://www.goftino.com/widget/' + WIDGET_ID;
        const l = localStorage.getItem('goftino_' + WIDGET_ID);
        s.async = true;
        s.src = l ? src + '?o=' + l : src;
        d.getElementsByTagName('head')[0].appendChild(s);
    }
    if (d.readyState === 'complete') g();
    else window.addEventListener('load', g, { once: true });
}

function applyVisibility(visible: boolean) {
    desiredVisible = visible;
    const w = window.Goftino;
    if (!w) return; // not loaded yet — the ready handler applies it once loaded
    if (appliedVisible === desiredVisible) return; // already in that state
    appliedVisible = desiredVisible;
    if (desiredVisible) w.reload();
    else w.destroy();
}

const Goftino = ({ visible }: { visible: boolean }) => {
    useEffect(() => {
        ensureScriptLoaded();

        const onReady = () => {
            appliedVisible = desiredVisible;
            if (!desiredVisible) window.Goftino?.destroy();
            // desiredVisible === true needs no action: the widget shows
            // itself by default once its script finishes loading.
        };
        window.addEventListener('goftino_ready', onReady);

        applyVisibility(visible);

        return () => window.removeEventListener('goftino_ready', onReady);
    }, [visible]);

    return null;
};

export default Goftino;
